import React, { useEffect, useState, useCallback } from "react";
import { fetchAdminInbox, replyMessage, markRead, archiveMessage } from "../lib/api";

const AdminInbox = () => {
  const [tab, setTab] = useState("unread"); // 'unread' | 'all' | 'suggestions'
  const [data, setData] = useState({ messages: [], unread_count: 0, total: 0 });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (tab === "unread") params.status = "unread";
    if (tab === "suggestions") params.source = "suggest";
    const res = await fetchAdminInbox(params);
    setData(res);
    if (selected) {
      const refreshed = res.messages.find((m) => m.id === selected.id);
      if (refreshed) setSelected(refreshed);
    }
  }, [tab, selected]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openMsg = async (m) => {
    setSelected(m);
    setReply("");
    if (m.status === "unread") {
      try { await markRead(m.id); load(); } catch (e) {}
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    setBusy(true);
    try {
      const res = await replyMessage(selected.id, reply);
      setFlash(`Reply sent from contact@nearnow.biz to ${res.would_send_to || "sender"} (MOCK).`);
      setReply("");
      load();
      setTimeout(() => setFlash(null), 3500);
    } catch (e) {
      setFlash("Reply failed.");
    } finally {
      setBusy(false);
    }
  };

  const archive = async (m) => {
    setBusy(true);
    try { await archiveMessage(m.id); setSelected(null); load(); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-inbox-page" style={{ padding: "32px 20px 80px", maxWidth: 1300, margin: "0 auto" }}>
      <p style={{ color: "#D4AF37", letterSpacing: 4, fontFamily: "Bebas Neue, sans-serif", fontSize: 16, margin: 0 }}>
        ADMIN INBOX · CONTACT@NEARNOW.BIZ
      </p>
      <h1 className="font-display" style={{ fontSize: 56, color: "#0A1628", margin: "8px 0 6px" }}>
        INBOX
      </h1>
      <p style={{ color: "#5a5d66", fontSize: 17 }}>
        {data.unread_count} unread · {data.total} on this view · replies send from contact@nearnow.biz <span className="mock-badge">MOCK</span>
      </p>

      {/* Tabs */}
      <div data-testid="inbox-tabs" style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
        {["unread", "all", "suggestions"].map((t) => (
          <button
            key={t}
            data-testid={`inbox-tab-${t}`}
            onClick={() => { setTab(t); setSelected(null); }}
            className={tab === t ? "btn-navy tap" : "btn-outline-navy tap"}
            style={{ minWidth: 120 }}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {flash && (
        <div data-testid="inbox-flash" style={{ marginTop: 14, padding: 12, background: "#D6F0E0", color: "#1A7A4A", borderRadius: 6 }}>
          {flash}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)", gap: 18, marginTop: 18 }} className="inbox-grid">
        {/* List */}
        <div data-testid="inbox-list" style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 8, padding: 8, maxHeight: 700, overflowY: "auto" }}>
          {data.messages.length === 0 && (
            <div style={{ padding: 24, color: "#8a8d96", textAlign: "center" }}>No messages on this view.</div>
          )}
          {data.messages.map((m) => (
            <button
              key={m.id}
              data-testid={`inbox-row-${m.id}`}
              onClick={() => openMsg(m)}
              style={{
                width: "100%", textAlign: "left", padding: "14px 14px", borderRadius: 6,
                background: selected?.id === m.id ? "#FFF8DC" : (m.status === "unread" ? "#F8F9FA" : "#fff"),
                border: selected?.id === m.id ? "2px solid #D4AF37" : "1px solid transparent",
                marginBottom: 6, cursor: "pointer", fontFamily: "DM Sans, sans-serif", color: "#1A1A2E",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <strong style={{ fontSize: 16 }}>{m.sender_name || (m.sender_email ? m.sender_email : "Anonymous")}</strong>
                <span style={{ fontSize: 12, color: "#8a8d96" }}>{(new Date(m.created_at)).toLocaleDateString()}</span>
              </div>
              <div style={{ fontSize: 15, color: "#0A1628", marginTop: 4 }}>{m.subject || "(no subject)"}</div>
              <div style={{ fontSize: 13, color: "#5a5d66", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {m.message_body}
              </div>
              <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{
                  fontSize: 10, padding: "2px 7px", borderRadius: 3,
                  background: m.source === "suggest" ? "#FFF3B0" : "#D6F0E0",
                  color: m.source === "suggest" ? "#6B5300" : "#1A7A4A",
                  fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1,
                }}>{m.source.toUpperCase()}</span>
                {m.status === "unread" && <span style={{ fontSize: 10, background: "#A32D2D", color: "#fff", padding: "2px 6px", borderRadius: 3, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1 }}>UNREAD</span>}
                {m.status === "replied" && <span style={{ fontSize: 10, background: "#1A7A4A", color: "#fff", padding: "2px 6px", borderRadius: 3, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1 }}>REPLIED</span>}
                {m.status === "archived" && <span style={{ fontSize: 10, background: "#8a8d96", color: "#fff", padding: "2px 6px", borderRadius: 3, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1 }}>ARCHIVED</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div data-testid="inbox-detail" style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 8, padding: 20, minHeight: 320 }}>
          {!selected ? (
            <div style={{ color: "#8a8d96", textAlign: "center", paddingTop: 80 }}>
              Pick a message to view.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div>
                  <div className="font-display" style={{ fontSize: 28, color: "#0A1628", letterSpacing: 1 }}>
                    {selected.subject || "(no subject)"}
                  </div>
                  <div style={{ fontSize: 14, color: "#5a5d66", marginTop: 4 }}>
                    From <strong>{selected.sender_name || "Anonymous"}</strong>
                    {selected.sender_email && <> · <a href={`mailto:${selected.sender_email}`}>{selected.sender_email}</a></>}
                    {selected.sender_phone && <> · {selected.sender_phone}</>}
                  </div>
                  <div style={{ fontSize: 13, color: "#8a8d96", marginTop: 2 }}>
                    {(new Date(selected.created_at)).toLocaleString()} · {selected.source} · {selected.category}
                  </div>
                </div>
                <button
                  data-testid="inbox-archive-btn"
                  onClick={() => archive(selected)}
                  className="btn-outline-navy tap"
                  style={{ fontSize: 14, padding: "8px 14px", minHeight: 40 }}
                >
                  ARCHIVE
                </button>
              </div>

              <div data-testid="inbox-body" style={{
                marginTop: 16, padding: 14, background: "#F8F9FA", borderRadius: 6, fontSize: 16, lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}>
                {selected.message_body}
              </div>

              {selected.admin_reply && (
                <div style={{ marginTop: 14, padding: 14, background: "#D6F0E0", borderRadius: 6 }}>
                  <div style={{ fontSize: 13, color: "#1A7A4A", fontWeight: 700, marginBottom: 6 }}>
                    YOUR REPLY · {selected.admin_replied_at && (new Date(selected.admin_replied_at)).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 15, whiteSpace: "pre-wrap" }}>{selected.admin_reply}</div>
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <label className="nn-label">Reply</label>
                <textarea
                  data-testid="inbox-reply-box"
                  className="nn-input"
                  style={{ minHeight: 110 }}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply. Sends from contact@nearnow.biz."
                />
                <button
                  data-testid="inbox-reply-send"
                  className="btn-gold tap"
                  style={{ marginTop: 10 }}
                  disabled={busy || !reply.trim()}
                  onClick={sendReply}
                >
                  {busy ? "SENDING..." : "SEND REPLY"}
                </button>
                <p style={{ fontSize: 12, color: "#8a8d96", marginTop: 8 }}>
                  <span className="mock-badge">MOCK</span> Outbound email simulated. Persists reply + sender + timestamp in DB.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInbox;
